// -- Constants
// This small controller has no static GSAP imports or preload hints.
if (!customElements.get('momax-reviews')) {
  // -- State and Variables
  let runtimePromise;
  let gateOpened = false;

  // -- Functions
  function loadScript(src) {
    // Reuse the runtime when an earlier homepage module has already loaded it.
    if ((src.includes('momax-gsap-') && window.gsap) || (src.includes('momax-scrolltrigger-') && window.ScrollTrigger)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const existing = [...document.scripts].find(script => script.src === new URL(src, document.baseURI).href);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => { script.remove(); reject(new Error('Reviews animation dependency failed to load')); };
      document.head.append(script);
    });
  }

  function runtime(element) {
    if (!runtimePromise) {
      runtimePromise = Promise.all([
        loadScript(element.dataset.gsapUrl),
        loadScript(element.dataset.scrollTriggerUrl),
        import(element.dataset.motionUrl)
      ]).then(([, , module]) => {
        window.gsap.registerPlugin(window.ScrollTrigger);
        return module;
      }).catch(error => { runtimePromise = undefined; throw error; });
    }
    return runtimePromise;
  }

  customElements.define('momax-reviews', class extends HTMLElement {
    // -- Functions
    watchGate() {
      this.gateObserver?.disconnect();
      const main = this.closest('#main, main, [role="main"]');
      const sections = main ? Array.from(main.querySelectorAll('.shopify-section')).filter(section =>
        !section.parentElement.closest('.shopify-section') && section.getClientRects().length && section.offsetHeight > 0
      ) : [];
      const second = sections[1];
      if (gateOpened) { this.allowed = true; this.startMotion(); return; }
      // A one-section page has no second module: keep its functional static layout.
      if (!second) return;
      const open = () => {
        gateOpened = true;
        this.allowed = true;
        this.gateObserver?.disconnect();
        this.startMotion();
      };
      const rect = second.getBoundingClientRect();
      // Covers restored scroll/deep links that already passed the second section.
      if (rect.top < innerHeight && (rect.bottom > 0 || scrollY > 0)) { open(); return; }
      this.gateObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) open();
      }, { rootMargin: '0px', threshold: 0 });
      this.gateObserver.observe(second);
    }

    async startMotion() {
      if (!this.allowed || !this.near || this.reduced.matches || this.motion || this.loading || !this.isConnected) return;
      const controller = this.controller;
      this.loading = true;
      try {
        const module = await runtime(this);
        if (controller.signal.aborted || this.reduced.matches) return;
        this.motion = module.mount(this, window.gsap, window.ScrollTrigger);
      } catch (error) {
        // Content is visible by default, including when the network is unavailable.
        console.warn('[momax-reviews]', error.message);
      } finally { this.loading = false; }
    }

    updatePagination() {
      const index = this.cards.reduce((best, card, i) =>
        Math.abs(card.offsetLeft - this.cards[0].offsetLeft - this.cardTrack.scrollLeft) <
        Math.abs(this.cards[best].offsetLeft - this.cards[0].offsetLeft - this.cardTrack.scrollLeft) ? i : best, 0);
      this.pages.forEach((page, i) => {
        if (i === index) page.setAttribute('aria-current', 'true');
        else page.removeAttribute('aria-current');
      });
      this.updateMobileProduct(index);
    }

    updateMobileProduct(index) {
      if (!this.mobileProducts || !this.mobileProducts.length) return;
      this.mobileProducts.forEach((item, i) => {
        if (i === index) item.classList.add('is-active');
        else item.classList.remove('is-active');
      });
    }

    selectCard(index) {
      const card = this.cards[index];
      if (!card) return;
      this.updateMobileProduct(index);
      this.cardTrack.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: this.reduced.matches ? 'auto' : 'smooth' });
    }

    // -- Lifecycle Hooks
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 740px)');
      this.cardTrack = this.querySelector('.momax-reviews__cards');
      this.cards = Array.from(this.querySelectorAll('[data-reviews-card]'));
      this.pages = Array.from(this.querySelectorAll('[data-reviews-page]'));
      this.mobileProducts = Array.from(this.querySelectorAll('[data-reviews-mobile-product]'));
      const pagination = this.querySelector('.momax-reviews__pagination');
      if (pagination) pagination.hidden = false;
      this.updatePagination();
      this.pages.forEach((page, index) => page.addEventListener('click', () => this.selectCard(index), { signal }));
      this.cardTrack.addEventListener('scroll', () => this.updatePagination(), { passive: true, signal });
      this.cardTrack.addEventListener('keydown', event => {
        if (!this.mobile.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let index = this.pages.findIndex(page => page.hasAttribute('aria-current'));
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.cards.length - 1;
        else index += event.key === 'ArrowRight' ? 1 : -1;
        this.selectCard(Math.max(0, Math.min(index, this.cards.length - 1)));
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        if (!this.mobile.matches) return;
        const index = this.cards.findIndex(card => card.dataset.blockId === event.detail.blockId);
        if (index !== -1) {
          this.selectCard(index);
          this.updateMobileProduct(index);
        }
        const product = Array.from(this.querySelectorAll('[data-reviews-product]')).find(item => item.dataset.blockId === event.detail.blockId);
        if (product) this.querySelector('.momax-reviews__products--desktop .momax-reviews__product-window')?.scrollTo({ left: product.offsetLeft, behavior: 'auto' });
      }, { signal });
      this.reduced.addEventListener('change', () => {
        if (this.reduced.matches) { this.motion?.(); this.motion = null; }
        else this.startMotion();
      }, { signal });
      // Both gates must open: second content section entered, and this module is near.
      this.nearObserver = new IntersectionObserver(entries => {
        this.near = entries.some(entry => entry.isIntersecting);
        this.startMotion();
      }, { rootMargin: '100% 0px', threshold: 0 });
      this.nearObserver.observe(this);
      document.addEventListener('shopify:section:reorder', () => this.watchGate(), { signal });
      document.addEventListener('shopify:section:load', () => this.watchGate(), { signal });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.watchGate(), { once: true, signal });
      else this.watchGate();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.gateObserver?.disconnect();
      this.nearObserver?.disconnect();
      this.motion?.();
      this.motion = null;
      this.loading = false;
    }
  });
}
